import * as Linking from "expo-linking";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import {
  Image,
  Platform,
  Pressable,
  Share,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { PageShell } from "../../../src/components/layout";
import { PublicEventExperience } from "../../../src/components/PublicEventExperience";
import { PublicFinalRestingPlace } from "../../../src/components/PublicFinalRestingPlace";
import { MonarchBrandLockup } from "../../../src/components/MonarchBrandLockup";
import {
  Button,
  ErrorState,
  Field,
  LoadingState,
  Panel,
} from "../../../src/components/ui";
import * as Clipboard from "../../../src/lib/clipboard";
import { ARRANGEMENT_PLANNER_START_URL } from "../../../src/lib/hosting-contract";
import {
  getPublishedCentralMemorialBySlug,
  getPublishedCentralMemorial,
  type PublicCentralMemorial,
  submitPublicCentralMemorialReport,
} from "../../../src/lib/public-central-memorial";
import { PublicGuestbookC4Form } from "../../../src/components/PublicGuestbookC4Form";
import { central, spacing } from "../../../src/theme/tokens";

const mariaPortrait = require("../../../assets/memorial/maria-elena-hernandez-portrait.jpg");

const monarchMemorialTheme = {
  canvas: "#F7F9FC",
  surface: "#FFFFFF",
  text: "#14233D",
  mutedText: "#4C5C73",
  border: "#D6DDE8",
  primary: "#10265E",
  primaryPressed: "#0B214A",
  accent: "#DC923D",
  hero: "#10265E",
  inverse: "#FFFFFF",
};

type ReportCategory =
  | "accessibility"
  | "accuracy"
  | "harmful_content"
  | "other"
  | "privacy";

const reportCategories: Array<{ id: ReportCategory; label: string }> = [
  { id: "accuracy", label: "Information is inaccurate" },
  { id: "privacy", label: "Privacy concern" },
  { id: "accessibility", label: "Accessibility barrier" },
  { id: "harmful_content", label: "Harmful content" },
  { id: "other", label: "Something else" },
];

const first = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] ?? "" : value ?? "";

function publicServiceNote(value: string) {
  return /WeHonor walkthrough|No real service details|provider deliveries/i.test(value)
    ? "The family welcomes loved ones to gather in Maria's honor. Current service information is listed below."
    : value;
}

export type PublishedCentralMemorialScreenProps = Readonly<{
  /** Present only for the canonical whnr.me-style global route. */
  globalSlug?: string;
}>;

/** The global route must never inherit a tenant compatibility canonical URL. */
export function publicMemorialCanonicalUrl(
  memorial: Pick<PublicCentralMemorial, "distribution" | "slug">,
  globalSlug?: string,
) {
  return globalSlug
    ? `https://wehonor.co/memorial/${encodeURIComponent(memorial.slug)}`
    : memorial.distribution.canonicalUrl;
}

export function PublishedCentralMemorialScreen({
  globalSlug,
}: PublishedCentralMemorialScreenProps) {
  const router = useRouter();
  const { width: viewportWidth } = useWindowDimensions();
  const contentRef = useRef<View | null>(null);
  const params = useLocalSearchParams<{
    event?: string | string[];
    funeralHomeId?: string | string[];
    mode?: string | string[];
    slug?: string | string[];
  }>();
  const tenantFuneralHomeId = first(params.funeralHomeId);
  const slug = globalSlug || first(params.slug);
  const eventId = first(params.event);
  const eventMode =
    first(params.mode) === "check-in" ? "check-in" : "rsvp";
  const [memorial, setMemorial] = useState<PublicCentralMemorial | null>(null);
  const [error, setError] = useState("");
  const [shareAnnouncement, setShareAnnouncement] = useState("");
  const [skipFocused, setSkipFocused] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] =
    useState<ReportCategory>("accuracy");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportAnnouncement, setReportAnnouncement] = useState("");
  const funeralHomeId = globalSlug
    ? memorial?.funeralHomeId ?? ""
    : tenantFuneralHomeId;
  const canonicalUrl = memorial
    ? publicMemorialCanonicalUrl(memorial, globalSlug)
    : "";

  useEffect(() => {
    if (!slug || (!globalSlug && !tenantFuneralHomeId)) return;
    const request = globalSlug
      ? getPublishedCentralMemorialBySlug(slug)
      : getPublishedCentralMemorial(tenantFuneralHomeId, slug);
    void request
      .then((published) => {
        setMemorial(published);
        if (published.slug !== slug) {
          router.replace((globalSlug
            ? `/memorial/${encodeURIComponent(published.slug)}`
            : `/memorial/${encodeURIComponent(tenantFuneralHomeId)}/${encodeURIComponent(published.slug)}`) as never);
        }
      })
      .catch(() => setError("This memorial is not available."));
  }, [globalSlug, router, slug, tenantFuneralHomeId]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const metadata = memorial?.distribution;
    const cleanups = [
      setHeadMeta("name", "robots", metadata?.robots ?? "noindex,nofollow,noarchive"),
      setHeadMeta("name", "referrer", "no-referrer"),
      setHeadMeta("name", "description", metadata?.description ?? "A memorial shared by the funeral home."),
      setHeadMeta("property", "og:title", metadata?.title ?? "Memorial | WeHonor"),
      setHeadMeta("property", "og:description", metadata?.description ?? "A memorial shared by the funeral home."),
      setHeadMeta("property", "og:url", canonicalUrl),
      setHeadMeta("property", "og:image", metadata?.imageUrl ?? ""),
      setHeadMeta("property", "og:image:alt", metadata?.imageAlt ?? ""),
      setCanonicalLink(canonicalUrl),
    ];
    const previousTitle = document.title;
    document.title = metadata?.title ?? "Memorial | WeHonor";
    return () => {
      cleanups.forEach((cleanup) => cleanup());
      document.title = previousTitle;
    };
  }, [canonicalUrl, memorial]);

  if (!slug || (!globalSlug && !tenantFuneralHomeId)) {
    return (
      <PageShell role="public" edgeToEdge showBrandBar={false} showDecorativeBackdrop={false} backgroundColor="#F7F9FC">
        <ErrorState
          title="Memorial Unavailable"
          description="The memorial link is incomplete."
        />
      </PageShell>
    );
  }
  if (error) {
    return (
      <PageShell role="public" edgeToEdge showBrandBar={false} showDecorativeBackdrop={false} backgroundColor="#F7F9FC">
        <ErrorState title="Memorial Unavailable" description={error} />
      </PageShell>
    );
  }
  if (!memorial) {
    return (
      <PageShell role="public" edgeToEdge showBrandBar={false} showDecorativeBackdrop={false} backgroundColor="#F7F9FC">
        <LoadingState
          label="Loading Memorial…"
          description="Preparing the published memorial."
        />
      </PageShell>
    );
  }

  const copyMemorialLink = async () => {
    await Clipboard.setStringAsync(canonicalUrl);
    setShareAnnouncement("Memorial link copied.");
  };

  const shareMemorial = async () => {
    setShareAnnouncement("");
    try {
      await Share.share({
        message: `${memorial.distribution.title}\n${canonicalUrl}`,
        title: memorial.distribution.title,
        url: canonicalUrl,
      });
    } catch {
      await copyMemorialLink();
    }
  };

  const submitReport = async () => {
    if (
      memorial.distribution.reporting !== "enabled" ||
      reportDetails.trim().length < 10 ||
      reportBusy
    ) return;
    setReportBusy(true);
    setReportAnnouncement("");
    try {
      const result = await submitPublicCentralMemorialReport({
        category: reportCategory,
        details: reportDetails,
        funeralHomeId,
        slug: memorial.slug,
      });
      setReportAnnouncement(result.message);
      setReportDetails("");
      setReportOpen(false);
    } catch {
      setReportAnnouncement(
        "This report could not be submitted. Please contact the funeral home.",
      );
    } finally {
      setReportBusy(false);
    }
  };

  const focusMemorialContent = () => {
    if (typeof document !== "undefined") {
      const target = document.getElementById("memorial-content");
      target?.setAttribute("tabindex", "-1");
      target?.focus();
      return;
    }
    (contentRef.current as unknown as { focus?: () => void })?.focus?.();
  };
  const focusServiceDetails = () => {
    if (typeof document !== "undefined") {
      const target = document.getElementById("memorial-service-events");
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
      target?.setAttribute("tabindex", "-1");
      target?.focus();
    }
  };
  const skipLinkKeyboardProps =
    Platform.OS === "web"
      ? {
          onKeyDown: (event: {
            key?: string;
            nativeEvent?: { key?: string };
            preventDefault?: () => void;
          }) => {
            const key = event.key ?? event.nativeEvent?.key;
            if (key !== "Enter" && key !== " ") return;
            event.preventDefault?.();
            focusMemorialContent();
          },
        }
      : {};

  return (
    <PageShell role="public" edgeToEdge showBrandBar={false} showDecorativeBackdrop={false} backgroundColor="#F7F9FC">
      <Pressable
        {...skipLinkKeyboardProps}
        accessibilityRole="link"
        onBlur={() => setSkipFocused(false)}
        onFocus={() => setSkipFocused(true)}
        onPress={focusMemorialContent}
        style={[
          styles.skipLink,
          skipFocused ? styles.skipLinkVisible : styles.skipLinkHidden,
        ]}
      >
        <Text style={styles.skipLinkText}>Skip to memorial content</Text>
      </Pressable>
      <View
        focusable
        nativeID="memorial-content"
        ref={contentRef}
        style={[styles.page, { backgroundColor: monarchMemorialTheme.canvas }]}
        tabIndex={-1}
      >
        <View
          accessibilityLabel={`Published Memorial for ${memorial.lovedOneName}`}
          style={styles.hero}
        >
          <View style={styles.heroTopline}>
            <MonarchBrandLockup size="public" />
            <Pressable
              accessibilityRole="button"
              onPress={() => void shareMemorial()}
              style={styles.heroShare}
            >
              <Text style={styles.heroShareText}>Share</Text>
            </Pressable>
          </View>
          <View style={[styles.heroBody, viewportWidth < 700 ? styles.heroBodyCompact : null]}>
            <View style={styles.heroMain}>
              <View style={styles.heroCopy}>
                <Text style={styles.heroEyebrow}>In loving memory of</Text>
                <Text
                  accessibilityRole="header"
                  style={[styles.heroTitle, viewportWidth < 700 ? styles.heroTitleCompact : null]}
                >
                  {memorial.lovedOneName}
                </Text>
                <Text style={styles.heroSubtitle}>
                  A life remembered with love and held close by family and friends.
                </Text>
              </View>
              <View style={styles.heroActions}>
                <Pressable
                  accessibilityRole="button"
                  onPress={focusServiceDetails}
                  style={styles.heroPrimaryAction}
                >
                  <Text style={styles.heroPrimaryActionText}>View service details</Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void copyMemorialLink()}
                  style={styles.heroSecondaryAction}
                >
                  <Text style={styles.heroSecondaryActionText}>Copy memorial link</Text>
                </Pressable>
              </View>
            </View>
            <View style={[styles.portraitFrame, viewportWidth < 700 ? styles.portraitFrameCompact : null]}>
              <Image
                accessibilityLabel={`Portrait of ${memorial.lovedOneName}`}
                source={mariaPortrait}
                resizeMode="cover"
                style={styles.portrait}
              />
            </View>
          </View>
        </View>

        <View style={styles.contentColumn}>
          <View style={styles.storySection}>
            <Text style={styles.sectionTitle}>A life remembered</Text>
            {memorial.obituary ? <Text style={styles.storyBody}>{memorial.obituary}</Text> : null}
            {memorial.serviceDetails ? (
              <View style={styles.serviceNote}>
                <Text style={styles.serviceNoteLabel}>From the family</Text>
                <Text style={styles.serviceNoteText}>{publicServiceNote(memorial.serviceDetails)}</Text>
              </View>
            ) : null}
            <View style={styles.actions}>
              {memorial.livestreamUrl ? <Button brand="monarch" compact label="Open Livestream" shape="rounded" onPress={() => void Linking.openURL(memorial.livestreamUrl)} /> : null}
              {memorial.donationUrl ? <Button brand="monarch" compact label="Donation Information" variant="secondary" shape="rounded" onPress={() => void Linking.openURL(memorial.donationUrl)} /> : null}
            </View>
          </View>

          <PublicFinalRestingPlace brand="monarch" funeralHomeId={funeralHomeId} slug={memorial.slug} />

          <View nativeID="memorial-service-events" style={styles.eventSection}>
            <PublicEventExperience
              funeralHomeId={funeralHomeId}
              initialEventId={eventId}
              initialMode={eventMode}
              memorial={memorial}
              slug={memorial.slug}
              brand="monarch"
            />
          </View>

          {memorial.guestbookEnabled ? (
            <View style={styles.guestbookSection}>
              <Text style={styles.sectionTitle}>Guestbook</Text>
              {memorial.guestbook.length ? memorial.guestbook.map((entry, index) => (
                <View key={`${entry.authorName}-${index}`} style={[styles.guestbookEntry, { borderTopColor: monarchMemorialTheme.border }]}>
                  <Text style={styles.guestbookAuthor}>{entry.authorName}</Text>
                  {entry.photoUrl ? <Image accessibilityLabel={`Photo shared by ${entry.authorName || "anonymous guest"}`} source={{ uri: entry.photoUrl }} resizeMode="contain" style={[styles.publicPhoto, { backgroundColor: central.surface.muted }]} /> : null}
                  <Text style={styles.storyBody}>{entry.content}</Text>
                  {entry.reply ? <Text style={styles.muted}>Reply from the funeral home: {entry.reply}</Text> : null}
                </View>
              )) : <Text style={styles.muted}>Guestbook responses are moderated by the funeral home.</Text>}
              <View style={[styles.form, { borderTopColor: monarchMemorialTheme.border }]}>
                <PublicGuestbookC4Form brand="monarch" funeralHomeId={funeralHomeId} slug={memorial.slug} />
              </View>
            </View>
          ) : null}

          <View accessibilityLabel="Share this memorial" style={styles.shareSection}>
            <Text style={styles.sectionTitle}>Share this memorial</Text>
            <View style={styles.actions}>
              <Button brand="monarch" compact label="Share" shape="rounded" onPress={() => void shareMemorial()} />
              <Button brand="monarch" compact label="Copy Link" shape="rounded" variant="secondary" onPress={() => void copyMemorialLink()} />
            </View>
            {shareAnnouncement ? <Text accessibilityLiveRegion="polite" style={styles.muted}>{shareAnnouncement}</Text> : null}
          </View>

          {memorial.distribution.reporting === "enabled" ? (
            <View style={styles.reportSection}>
              <Pressable accessibilityRole="button" accessibilityState={{ expanded: reportOpen }} onPress={() => setReportOpen((value) => !value)} style={styles.reportToggle}>
                <Text style={styles.reportToggleText}>Report a concern</Text>
              </Pressable>
              {reportOpen ? (
                <Panel style={[styles.panel, styles.interactivePanel]}>
                  <Text style={styles.formTitle}>What should the funeral home review?</Text>
                  <View accessibilityRole="radiogroup" style={styles.reportChoices}>
                    {reportCategories.map((category) => (
                      <Pressable accessibilityRole="radio" accessibilityState={{ checked: reportCategory === category.id }} key={category.id} onPress={() => setReportCategory(category.id)} style={[styles.reportChoice, { borderColor: reportCategory === category.id ? monarchMemorialTheme.primary : monarchMemorialTheme.border }]}>
                        <Text style={styles.consentText}>{category.label}</Text>
                      </Pressable>
                    ))}
                  </View>
                  <Field brand="monarch" label="Details" hint="Do not include your contact information." multiline required value={reportDetails} onChangeText={setReportDetails} placeholder="Describe what the funeral home should review" inputStyle={styles.messageInput} />
                  {reportAnnouncement ? <Text accessibilityLiveRegion="polite" style={styles.muted}>{reportAnnouncement}</Text> : null}
                  <Button brand="monarch" label="Submit report" disabled={reportDetails.trim().length < 10 || reportBusy} loading={reportBusy} onPress={() => void submitReport()} shape="rounded" />
                </Panel>
              ) : null}
            </View>
          ) : null}

          <FamilyAccessFooter borderColor={monarchMemorialTheme.border} focusColor={monarchMemorialTheme.primary} mutedColor={monarchMemorialTheme.mutedText} textColor={monarchMemorialTheme.text} />
        </View>
      </View>
    </PageShell>
  );
}

export default PublishedCentralMemorialScreen;

function FamilyAccessFooter({
  borderColor,
  focusColor,
  mutedColor,
  textColor,
}: {
  borderColor: string;
  focusColor: string;
  mutedColor: string;
  textColor: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <View style={[styles.familyAccessFooter, { borderTopColor: borderColor }]}>
      <Text style={[styles.familyAccessContext, { color: mutedColor }]}>
        Are you helping with arrangements?
      </Text>
      <Pressable
        accessibilityHint="Opens the Arrangement Planner."
        accessibilityLabel="Family access — open Arrangement Planner"
        accessibilityRole="link"
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        onPress={() => void Linking.openURL(ARRANGEMENT_PLANNER_START_URL)}
        style={[
          styles.familyAccessLink,
          focused ? { borderColor: focusColor } : null,
        ]}
      >
        <Text style={[styles.familyAccessLabel, { color: textColor }]}>
          Family access
        </Text>
        <Text style={[styles.familyAccessDetail, { color: mutedColor }]}>
          Open the Arrangement Planner
        </Text>
      </Pressable>
    </View>
  );
}

function setHeadMeta(
  attribute: "name" | "property",
  key: string,
  content: string,
) {
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.querySelector(selector);
  const created = !element;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  const previous = element.getAttribute("content");
  if (content) element.setAttribute("content", content);
  else element.removeAttribute("content");
  return () => {
    if (created) element?.remove();
    else if (previous === null) element?.removeAttribute("content");
    else element?.setAttribute("content", previous);
  };
}

function setCanonicalLink(href: string) {
  let element = document.querySelector('link[rel="canonical"]');
  const created = !element;
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  const previous = element.getAttribute("href");
  if (href) element.setAttribute("href", href);
  else element.removeAttribute("href");
  return () => {
    if (created) element?.remove();
    else if (previous === null) element?.removeAttribute("href");
    else element?.setAttribute("href", previous);
  };
}

const styles = StyleSheet.create({
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  contentColumn: {
    alignSelf: "center",
    gap: spacing.xl,
    maxWidth: 1080,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xxl,
    width: "100%",
  },
  announcement: {
    fontFamily: "Manrope_700Bold",
    fontSize: 14,
  },
  body: {
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 25,
    maxWidth: 720,
  },
  checkbox: {
    alignItems: "center",
    borderRadius: 4,
    borderWidth: 1,
    fontSize: 16,
    height: 24,
    lineHeight: 22,
    textAlign: "center",
    width: 24,
  },
  consent: {
    alignItems: "flex-start",
    flexDirection: "row",
    gap: spacing.sm,
    minHeight: 44,
  },
  consentText: {
    flex: 1,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 20,
  },
  error: {
    color: "#b42318",
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
  },
  eventSection: {
    gap: spacing.md,
    width: "100%",
  },
  eyebrow: {
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  familyAccessContext: {
    color: central.text.muted,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
    lineHeight: 18,
  },
  familyAccessDetail: {
    color: central.text.muted,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
    lineHeight: 18,
  },
  familyAccessFooter: {
    alignItems: "center",
    borderTopWidth: 1,
    gap: spacing.xs,
    marginTop: spacing.md,
    paddingVertical: spacing.lg,
  },
  familyAccessLabel: {
    color: central.text.secondary,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
    textDecorationLine: "underline",
  },
  familyAccessLink: {
    alignItems: "center",
    borderColor: "transparent",
    borderRadius: central.radius.control,
    borderWidth: 2,
    gap: 2,
    justifyContent: "center",
    minHeight: 48,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  formTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 18,
  },
  form: {
    borderTopWidth: 1,
    gap: spacing.md,
    paddingTop: spacing.md,
  },
  guestbookAuthor: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 14,
  },
  guestbookEntry: {
    borderTopWidth: 1,
    gap: spacing.xs,
    paddingTop: spacing.sm,
  },
  guestbookSection: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
    borderRadius: central.radius.card,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  header: {
    gap: spacing.xs,
    paddingBottom: spacing.sm,
  },
  intro: {
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 22,
  },
  interactiveMuted: {
    color: central.text.muted,
  },
  interactivePanel: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
  },
  interactiveText: {
    color: central.text.primary,
  },
  label: {
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    textTransform: "uppercase",
  },
  logo: {
    borderRadius: central.radius.control,
    height: 64,
    marginBottom: spacing.sm,
    width: 128,
  },
  messageInput: {
    minHeight: 120,
    textAlignVertical: "top",
  },
  muted: {
    color: central.text.muted,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 19,
  },
  page: {
    alignSelf: "center",
    gap: spacing.xl,
    maxWidth: 1280,
    padding: 0,
    width: "100%",
  },
  panel: {
    borderWidth: 1,
    gap: spacing.md,
  },
  photoActions: {
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  publicPhoto: {
    borderRadius: central.radius.card,
    height: 240,
    maxWidth: "100%",
    width: 360,
  },
  reportChoice: {
    borderRadius: central.radius.control,
    borderWidth: 2,
    minHeight: 44,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  reportChoices: {
    gap: spacing.xs,
  },
  reportSection: {
    gap: spacing.sm,
  },
  reportToggle: {
    alignSelf: "flex-start",
    minHeight: 44,
    paddingVertical: spacing.sm,
  },
  reportToggleText: {
    color: central.action.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    textDecorationLine: "underline",
  },
  section: {
    borderTopWidth: 1,
    gap: spacing.xs,
    paddingTop: spacing.md,
  },
  shareSection: {
    borderTopColor: central.border.subtle,
    borderTopWidth: 1,
    gap: spacing.sm,
    paddingTop: spacing.lg,
  },
  serviceNote: {
    borderTopColor: central.border.subtle,
    borderTopWidth: 1,
    gap: spacing.xs,
    marginTop: spacing.sm,
    paddingTop: spacing.md,
  },
  serviceNoteLabel: {
    color: central.accent.primary,
    fontFamily: central.typography.label,
    fontSize: 12,
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  serviceNoteText: {
    color: central.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 23,
  },
  sectionTitle: {
    color: central.text.primary,
    fontFamily: central.typography.display,
    fontSize: 34,
    lineHeight: 40,
  },
  storyBody: {
    color: central.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 17,
    lineHeight: 28,
    maxWidth: 760,
  },
  storySection: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
    borderRadius: central.radius.card,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.xl,
  },
  hero: {
    backgroundColor: central.surface.raised,
    borderBottomColor: central.border.subtle,
    borderBottomWidth: 1,
    gap: 0,
    paddingBottom: 48,
    width: "100%",
  },
  heroBody: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    gap: 48,
    maxWidth: 1080,
    paddingHorizontal: spacing.xl,
    paddingTop: 48,
    width: "100%",
  },
  heroBodyCompact: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: spacing.xl,
    paddingTop: 40,
  },
  heroMain: {
    flex: 1,
    minWidth: 0,
  },
  heroActions: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
    justifyContent: "flex-start",
    paddingHorizontal: 0,
    paddingTop: spacing.xl,
  },
  heroCopy: {
    alignItems: "flex-start",
    gap: spacing.sm,
    maxWidth: 760,
    paddingHorizontal: 0,
    paddingTop: 0,
    width: "100%",
  },
  heroEyebrow: {
    color: central.accent.primary,
    fontFamily: central.typography.label,
    fontSize: 13,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  heroPrimaryAction: {
    backgroundColor: monarchMemorialTheme.accent,
    borderRadius: 10,
    minHeight: 48,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  heroPrimaryActionText: {
    color: monarchMemorialTheme.text,
    fontFamily: central.typography.label,
    fontSize: 14,
  },
  heroSecondaryAction: {
    borderColor: monarchMemorialTheme.primary,
    borderRadius: 10,
    borderWidth: 1,
    minHeight: 48,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  heroSecondaryActionText: {
    color: monarchMemorialTheme.primary,
    fontFamily: central.typography.label,
    fontSize: 14,
  },
  heroShare: {
    alignItems: "center",
    borderColor: "rgba(255,255,255,0.52)",
    borderRadius: 10,
    borderWidth: 1,
    minHeight: 44,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  heroShareText: {
    color: central.text.inverse,
    fontFamily: central.typography.label,
    fontSize: 13,
  },
  heroSubtitle: {
    color: monarchMemorialTheme.mutedText,
    fontFamily: central.typography.body,
    fontSize: 16,
    lineHeight: 24,
    maxWidth: 620,
    textAlign: "left",
  },
  portraitFrame: {
    aspectRatio: 4 / 5,
    backgroundColor: "#E8EDF3",
    borderColor: "#D6DDE8",
    borderRadius: 14,
    borderWidth: 1,
    maxWidth: 300,
    overflow: "hidden",
    width: "32%",
  },
  portraitFrameCompact: {
    alignSelf: "center",
    maxWidth: 300,
    width: "100%",
  },
  portrait: {
    height: "100%",
    width: "100%",
  },
  heroTitle: {
    color: monarchMemorialTheme.text,
    fontFamily: central.typography.display,
    fontSize: 72,
    letterSpacing: -1.2,
    lineHeight: 78,
    textAlign: "left",
  },
  heroTitleCompact: {
    fontSize: 52,
    lineHeight: 56,
  },
  heroTopline: {
    alignItems: "center",
    backgroundColor: monarchMemorialTheme.hero,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: spacing.xl,
    paddingVertical: 18,
    width: "100%",
  },
  skipLink: {
    backgroundColor: central.action.primary,
    borderRadius: central.radius.control,
    left: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    position: "absolute",
    top: spacing.sm,
    zIndex: 10,
  },
  skipLinkHidden: {
    opacity: 0,
    transform: [{ translateY: -80 }],
  },
  skipLinkText: {
    color: central.text.inverse,
    fontFamily: "Manrope_700Bold",
    fontSize: 14,
  },
  skipLinkVisible: {
    opacity: 1,
    transform: [{ translateY: 0 }],
  },
  title: {
    color: central.text.primary,
    fontFamily: central.typography.display,
    fontSize: 36,
    letterSpacing: -0.6,
    lineHeight: 42,
  },
});
